import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  Clock, 
  Users, 
  Award, 
  Star, 
  Calendar,
  User,
  CheckCircle,
  Heart,
  Share2,
  MapPin,
  Phone,
  Mail,
  Shield,
  BookOpen,
  AlertCircle
} from 'lucide-react'
import { useBooking } from '../contexts/BookingContext'
import { getClassById } from '../data'
import { DanceClass, getContactEmail, getContactEmailLink, getStudioPhone, isScheduleInFuture, isValidScheduleTime, formatTimeRangeWithAMPM, formatReadableDateParts } from '../core'
import { useScheduleData } from '../hooks'

export default function ClassDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { setIsBookingOpen, setIsDemo, setSelectedClass } = useBooking()
  const [danceClass, setDanceClass] = useState<DanceClass | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Use custom hook for schedule data
  const { 
    allClassSchedules: schedules, 
    isLoading: schedulesLoading,
    error: schedulesError
  } = useScheduleData({ 
    danceClass: danceClass, 
    isDemo: false 
  })

  useEffect(() => {
    const loadClassData = async () => {
      if (id) {
        const classId = parseInt(id)
        const classData = getClassById(classId)
        
        if (classData) {
          setDanceClass(classData)
        } else {
          navigate('/classes')
        }
        setIsLoading(false)
      }
    }
    
    loadClassData()
  }, [id, navigate])

  const handleBookingClick = (isDemo = false) => {
    if (danceClass) {
      setSelectedClass(danceClass)
      setIsDemo(isDemo)
      setIsBookingOpen(true)
    }
  }


  if (isLoading || schedulesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading class details...</p>
        </div>
      </div>
    )
  }

  if (!danceClass) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Class Not Found</h1>
          <button
            onClick={() => navigate('/classes')}
            className="btn btn-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Classes
          </button>
        </div>
      </div>
    )
  }

  // Filter schedules to show only future classes (based on current IST date and time)
  const futureSchedules = schedules.filter(schedule => 
    isScheduleInFuture(schedule.specificDate, schedule.startTime)
  )
  
  const regularSchedules = futureSchedules.filter(s => !s.isDemo)
  const demoSchedules = futureSchedules.filter(s => s.isDemo)

  // Helper function to get capacity status
  const getCapacityStatus = (schedule: any) => {
    const remaining = schedule.remainingSpots || schedule.maxCapacity
    const isFullyBooked = schedule.isFullyBooked || remaining === 0
    const isWithinBuffer = !isValidScheduleTime(schedule.specificDate, schedule.startTime)
    
    if (isFullyBooked) return { status: 'full', text: 'Fully Booked', color: 'text-red-600' }
    if (isWithinBuffer) return { status: 'buffer', text: 'Cannot book within 1 hour', color: 'text-orange-600' }
    if (remaining <= 3) return { status: 'low', text: `${remaining} spots left`, color: 'text-yellow-600' }
    return { status: 'available', text: `${remaining} spots available`, color: 'text-green-600' }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/classes')}
              className="flex items-center text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Classes
            </button>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-500 transition-colors">
                <Heart className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="gradient-bg text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="bg-green-500/20 text-white border border-green-300/30 px-3 py-1 rounded-full text-sm font-medium">
                Free Demo Available
              </span>
              <span className="bg-blue-500/20 text-white border border-blue-300/30 px-3 py-1 rounded-full text-sm font-medium">
                {danceClass.category.charAt(0).toUpperCase() + danceClass.category.slice(1)} Dance
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {danceClass.name}
            </h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
              {danceClass.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => handleBookingClick(false)}
                className="btn bg-white text-primary-600 hover:bg-gray-100 text-lg px-8 py-3"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book This Class
              </button>
              <button
                onClick={() => handleBookingClick(true)}
                className="btn border-2 border-white text-white hover:bg-white hover:text-primary-600 text-lg px-8 py-3"
              >
                <Star className="w-5 h-5 mr-2" />
                Try Free Demo Session
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Info Cards */}
      <section className="py-8 px-6 -mt-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-4">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center"
            >
              <Clock className="w-8 h-8 text-primary-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-800 dark:text-gray-200">Duration</h3>
              <p className="text-gray-600 dark:text-gray-400">{danceClass.duration} minutes</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">Complete session</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center"
            >
              <Users className="w-8 h-8 text-secondary-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-800 dark:text-gray-200">Class Size</h3>
              <p className="text-gray-600 dark:text-gray-400">Max {danceClass.maxCapacity} students</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">Personal attention</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center"
            >
              <Award className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-800 dark:text-gray-200">Level</h3>
              <p className="text-gray-600 dark:text-gray-400">{danceClass.level}</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">Suitable for all skill levels</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center"
            >
              <Award className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-800 dark:text-gray-200">Category</h3>
              <p className="text-gray-600 dark:text-gray-400">{danceClass.category.charAt(0).toUpperCase() + danceClass.category.slice(1)}</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">{danceClass.ageGroup}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* About Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
              >
                <img 
                  src={danceClass.image} 
                  alt={danceClass.name}
                  className="w-full h-96 object-cover"
                />
                <div className="p-8">
                  <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{danceClass.name}</h2>
                  <div className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                    {danceClass.longDescription?.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-primary-50 dark:bg-primary-700/20 p-4 rounded-lg">
                      <h3 className="font-semibold text-lg mb-3 text-primary-800 dark:text-white">What You'll Learn</h3>
                      <ul className="space-y-2 text-gray-700 dark:text-gray-200">
                        {danceClass.features?.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="bg-secondary-50 dark:bg-secondary-700/20 p-4 rounded-lg">
                      <h3 className="font-semibold text-lg mb-3 text-secondary-800 dark:text-white">Key Benefits</h3>
                      <ul className="space-y-2 text-gray-700 dark:text-gray-200">
                        {danceClass.benefits?.map((benefit, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-secondary-600 dark:text-secondary-400 mt-0.5 flex-shrink-0" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Class Schedule */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8"
              >
                <h3 className="text-xl font-bold mb-6 flex items-center text-gray-900 dark:text-white">
                  <Calendar className="w-6 h-6 mr-2 text-primary-600" />
                  Class Schedule
                </h3>
                
                {schedulesError ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-red-600 mb-2">Service Temporarily Unavailable</h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      We're experiencing technical difficulties. Please try again after some time.
                    </p>
                  </div>
                ) : (
                  <>
                    {regularSchedules.length > 0 && (
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Regular Classes</h4>
                    <div className="grid gap-3">
                      {regularSchedules.map((schedule) => {
                        const capacityStatus = getCapacityStatus(schedule)
                        const isDisabled = capacityStatus.status === 'full' || capacityStatus.status === 'buffer'
                        
                        return (
                          <div 
                            key={schedule.id} 
                            className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                              isDisabled 
                                ? 'bg-gray-100 dark:bg-gray-600 opacity-75' 
                                : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                            }`}
                          >
                            <div className="flex items-center">
                              <Calendar className="w-5 h-5 mr-3 text-primary-500" />
                              <div>
                                <span className="font-medium text-lg text-gray-900 dark:text-white">
                                  {(() => {
                                    const { day, suffix, month, year } = formatReadableDateParts(schedule.specificDate)
                                    return (
                                      <>
                                        {day}<sup className="text-sm">{suffix}</sup> {month} {year}
                                      </>
                                    )
                                  })()}
                                </span>
                                <div className="flex items-center mt-1">
                                  <Users className="w-4 h-4 mr-1 text-gray-500" />
                                  <span className="text-sm text-gray-500 dark:text-gray-400">
                                    {schedule.currentEnrollment || 0}/{schedule.maxCapacity} enrolled
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col space-y-1">
                              <div className="flex items-center justify-end text-gray-600 dark:text-gray-300">
                                <Clock className="w-5 h-5 mr-2 flex-shrink-0" />
                                <span className="font-medium text-right min-w-[140px]">{formatTimeRangeWithAMPM(schedule.startTime, schedule.endTime)}</span>
                              </div>
                              <div className="flex items-center justify-end ml-7">
                                {capacityStatus.status === 'buffer' && (
                                  <AlertCircle className="w-4 h-4 mr-1 text-orange-500" />
                                )}
                                <span className={`text-sm font-medium text-right ${capacityStatus.color}`}>
                                  {capacityStatus.text}
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                    )}

                    {demoSchedules.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Free Demo Sessions</h4>
                        <div className="grid gap-3">
                          {demoSchedules.map((schedule) => {
                            const capacityStatus = getCapacityStatus(schedule)
                            const isDisabled = capacityStatus.status === 'full' || capacityStatus.status === 'buffer'
                            
                            return (
                              <div 
                                key={schedule.id} 
                                className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                                  isDisabled 
                                    ? 'bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 opacity-75' 
                                    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30'
                                }`}
                              >
                                <div className="flex items-center">
                              <Star className="w-5 h-5 mr-3 text-green-500" />
                              <div>
                                <span className="font-medium text-lg text-gray-900 dark:text-white">
                                  {schedule.specificDate} (FREE)
                                </span>
                                <div className="flex items-center mt-1">
                                  <Users className="w-4 h-4 mr-1 text-gray-500" />
                                  <span className="text-sm text-gray-500 dark:text-gray-400">
                                    {schedule.currentEnrollment || 0}/{schedule.maxCapacity} enrolled
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col space-y-1">
                              <div className="flex items-center justify-end text-gray-600 dark:text-gray-300">
                                <Clock className="w-5 h-5 mr-2 flex-shrink-0" />
                                <span className="font-medium text-right min-w-[140px]">{formatTimeRangeWithAMPM(schedule.startTime, schedule.endTime)}</span>
                              </div>
                              <div className="flex items-center justify-end ml-7">
                                {capacityStatus.status === 'buffer' && (
                                  <AlertCircle className="w-4 h-4 mr-1 text-orange-500" />
                                )}
                                <span className={`text-sm font-medium text-right ${capacityStatus.color}`}>
                                  {capacityStatus.text}
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                    )}

                    {regularSchedules.length === 0 && demoSchedules.length === 0 && (
                      <div className="text-center py-8">
                        <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h4 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">No Future Schedules Available</h4>
                        <p className="text-gray-500 dark:text-gray-500 mb-4">
                          No future class schedules are currently available. Please contact us for more information about upcoming classes.
                        </p>
                        <button
                          onClick={() => navigate('/contact')}
                          className="btn btn-primary"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Contact Us
                        </button>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-8">
              {/* Class Details Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-primary-600" />
                  Class Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Instructor</span>
                    <p className="font-medium text-gray-900 dark:text-white">{danceClass.instructor}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Delivery Mode</span>
                    <p className="font-medium text-gray-900 dark:text-white">{danceClass.deliveryMode}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Age Group</span>
                    <p className="font-medium text-gray-900 dark:text-white">{danceClass.ageGroup}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Duration</span>
                    <p className="font-medium text-gray-900 dark:text-white">{danceClass.duration} minutes per session</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Level</span>
                    <p className="font-medium text-primary-600 text-lg">{danceClass.level}</p>
                  </div>
                </div>
              </motion.div>

              {/* Requirements */}
              {danceClass.requirements && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <BookOpen className="w-5 h-5 mr-2 text-primary-600" />
                    What to Bring
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">{danceClass.requirements}</p>
                </motion.div>
              )}

              {/* Equipment */}
              {danceClass.equipment && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-primary-600" />
                    Equipment Needed
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">{danceClass.equipment}</p>
                </motion.div>
              )}

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-700/20 dark:to-secondary-700/20 rounded-lg p-6 shadow-lg"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Need Help?</h3>
                <div className="space-y-3">
                  <div className="flex items-center text-gray-600 dark:text-gray-300">
                    <Phone className="w-4 h-4 mr-2" />
                    <span>{getStudioPhone()}</span>
                  </div>
                  <div className="flex items-center text-gray-600 dark:text-gray-300">
                    <Mail className="w-4 h-4 mr-2" />
                    <a href={getContactEmailLink()} className="hover:text-primary-600 transition-colors">
                      {getContactEmail()}
                    </a>
                  </div>
                  <div className="flex items-center text-gray-600 dark:text-gray-300">
                    <MapPin className="w-4 h-4 mr-2" />
                    <span>Bangalore, Karnataka</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
